import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { Box } from "@mui/material";

const LoadingOverlay = () => {
  const isPageLoading = useSelector((state) => state.loading.isPageLoading);
  const isLoading = useSelector((state) => state.auth.isLoading);
  // True from the instant an Inbox chat is clicked until its messages commit to
  // Redux. Used to be lumped into the FULL dark overlay below — Rahim's correction
  // (2026-08-19): a chat loading should never dim the page, only get the same thin
  // green bar a route navigation gets (the chat pane's own skeleton already covers
  // the rest — see ChatBody.jsx). Previously this only showed for a ~300ms flash
  // right at the END of a chat load (once navigate() fires and the URL updates);
  // now it spans the actual loading duration.
  const isContactSwitching = useSelector(
    (state) => !!(state.inboxMessage?.expectedContactId)
  );
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    setIsNavigating(true);
    const timer = setTimeout(() => setIsNavigating(false), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const shouldShow = isPageLoading || isLoading || isNavigating || isContactSwitching;

  // Real page/auth loads get the full dark overlay; a route nav or a chat switch
  // only ever gets the thin non-blocking bar, never the dark tint.
  const isThinBar = (isNavigating || isContactSwitching) && !isPageLoading && !isLoading;

  return (
    shouldShow && (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: isThinBar ? "auto" : "100%",
          backgroundColor: isThinBar ? "transparent" : "rgba(0, 0, 0, 0.1)",
          zIndex: 9999,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          pointerEvents: "none",
          transition: "opacity 0.2s ease-in-out",
          opacity: shouldShow ? 1 : 0,
        }}
      >
        <LinearProgress
          sx={{
            width: "100%",
            height: isThinBar ? "3px" : "4px",
            backgroundColor: isThinBar ? "transparent" : "rgba(255, 255, 255, 0.25)",
            "& .MuiLinearProgress-bar": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#4fc387" : "#0e9046",
              boxShadow: isThinBar
                ? "none"
                : "0 0 8px rgba(14, 144, 70, 0.5)",
            },
          }}
        />
      </Box>
    )
  );
};

export default LoadingOverlay;
